import { useCallback, useMemo, useRef } from 'react';
import type { MouseEvent, VoidFunctionComponent } from 'react';
import type { NodeRendererProps } from 'react-arborist';

import { useQuery } from '../../../../contexts/RouterContext';
import useRejectSnippets from '../../../../hooks/api/snippets/useRejectSnippets';
import useUnrejectSnippets from '../../../../hooks/api/snippets/useUnrejectSnippets';
import { useOnHover } from '../../../../hooks/useOnHover';
import Button from '../../../generic/Button/Button';
import { FileTreeNode } from '../../../generic/FileTree';
import useNestingLines from '../../../generic/FileTree/hooks/useNestingLines';
import { icons } from '../../../generic/Icon';
import { Menu, MenuItem } from '../../../generic/Menu';
import useComparedSnippetsInfo from '../hooks/useComparedSnippetsInfo';
import type { SnippetsQueryParams } from '../../../../../shared/snippets';
import type { SnippetTreeNodeData } from '../types';

import styles from './SnippetsTreeViewNode.module.less';

/**
 * Renders a single Node in the SnippetsTreeView. Gets its props from
 * `react-arborist` directly (see NodeRendererProps). This includes inline styles
 * which are applied directly to the element.
 */
const SnippetsTreeViewNode: VoidFunctionComponent<NodeRendererProps<SnippetTreeNodeData>> = ({
  node,
  style,
}) => {
  const isDirectory = node.data.type === 'directory';
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const { isHovered, onMouseEnter, onMouseLeave } = useOnHover();

  const shouldShowMenu = isHovered;

  // Function to check if the click event was on the menu button
  const isMenuButtonClick = useCallback((event: MouseEvent) =>
    menuButtonRef.current?.contains(event.target as Node), [menuButtonRef]);

  //  Handle clicking on the entire row.
  const handleSelectRow = (event: MouseEvent) => {
    //  Ignore clicks if they are just opening the menu
    if (isMenuButtonClick(event)) {
      return;
    }

    //  Handle selecting a node.
    node.handleClick(event);
  };

  const handleToggleDirectory = () => {
    //  "isInternal" is a react-arborist-ism for "this is a directory".
    //  Calling `node.toggle` will toggle the open/closed state of these nodes.
    if (node.isInternal) {
      node.toggle();
    }
  };

  const comparedSnippetsInfo = useComparedSnippetsInfo();

  // If we are comparing snippets and the status is "removed", then we need to [un]reject
  // snippets based on the older revision because we fetch them from Sparkle based on a
  // specific user revision.
  const isComparingRemovedSnippets = comparedSnippetsInfo.isComparing && comparedSnippetsInfo.status === 'removed';
  const relevantUserRevisionLocator = isComparingRemovedSnippets
    ? comparedSnippetsInfo.olderRevisionLocator
    : node.data.userRevisionLocator;

  const [queryParams] = useQuery<SnippetsQueryParams>();

  // Ensure we invalidate the current revision's snippets list since
  // [un]rejections target the older revision when comparing removed snippets.
  const { mutate: rejectSnippets } = useRejectSnippets(
    relevantUserRevisionLocator,
    { revisionLocator: node.data.userRevisionLocator },
  );
  const { mutate: unrejectSnippets } = useUnrejectSnippets(
    relevantUserRevisionLocator,
    { revisionLocator: node.data.userRevisionLocator },
  );

  // Generate nesting lines background for the current node level
  const nestingLinesBackgroundStyles = useNestingLines(node.level);
  const enhancedStyle = useMemo(() => ({
    ...style,
    ...nestingLinesBackgroundStyles,
    overflow: 'hidden',
  }), [nestingLinesBackgroundStyles, style]);

  return (
    <FileTreeNode
      style={enhancedStyle}
      onClick={handleSelectRow}
      isSelected={node.isSelected}
      isDirectory={isDirectory}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <FileTreeNode.Arrow
        isOpen={node.isOpen}
        isDirectory={isDirectory}
        onToggleDirectory={handleToggleDirectory}
      />
      {!isDirectory && (<FileTreeNode.Icon />)}
      <FileTreeNode.Text text={node.data.name} />
      {shouldShowMenu ? (
        <FileTreeNode.Rest>
          <div className={styles.treeNodeMenu}>
            <Menu
              direction="right"
              onClick={(event: MouseEvent) => {
                //  Prevent clicks on the menu from automatically collapsing/opening the tree.
                event.stopPropagation();
              }}
              menuButton={
                <Button
                  ref={menuButtonRef}
                  type="text"
                  size="medium"
                  icon={icons.ellipsisHorizontal}
                  onClick={(event: MouseEvent) => {
                    //  Prevent clicks on the menu button from automatically collapsing/opening the tree.
                    event.stopPropagation();
                  }}
                />
              }
            >
              <MenuItem onClick={() => rejectSnippets(queryParams)}>
                Reject all
              </MenuItem>
              <MenuItem onClick={() => unrejectSnippets(queryParams)}>
                Stop rejecting
              </MenuItem>
            </Menu>
          </div>
        </FileTreeNode.Rest>
      ) : (
        <FileTreeNode.Rest>
          <div className={styles.treeNodeCount}>{node.data.count}</div>
        </FileTreeNode.Rest>
      )}
    </FileTreeNode>
  );
};

export default SnippetsTreeViewNode;
